import React, { useState, useEffect, useRef } from 'react';
import { User } from 'firebase/auth';
import { BsPinAngle, BsPinAngleFill, BsSend, BsChatDots, BsFullscreen, BsFullscreenExit } from "react-icons/bs";
import { 
  ChatMessage, 
  sendChatMessage, 
  loadRecentMessages, 
  subscribeToChatMessages, 
  getOnlineUserCount 
} from '../../services/chatService';
import ToolHeader from './ToolHeader';
import './ChatTab.css';

interface ChatTabProps {
  user: User | null;
  hasPaidSubscription: boolean;
  onShowPaymentPopup: () => void;
  onShowLoginInfo: () => void;
  isWalkthroughMode?: boolean;
  isPinned: boolean;
  onTogglePin: (pinned: boolean) => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: (fullscreen: boolean) => void;
}

const ChatTab: React.FC<ChatTabProps> = ({
  user,
  hasPaidSubscription,
  onShowPaymentPopup,
  onShowLoginInfo,
  isWalkthroughMode = false,
  isPinned,
  onTogglePin,
  isFullscreen,
  onToggleFullscreen
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Demo messages for walkthrough mode
  const demoMessages: ChatMessage[] = [
    {
      id: '1',
      userId: 'demo1',
      username: 'Alex',
      message: 'Hey everyone! Working on a new React project today 🚀',
      timestamp: new Date(Date.now() - 300000), // 5 minutes ago
    },
    {
      id: '2',
      userId: 'demo2',
      username: 'Sarah',
      message: 'Nice! I\'m debugging some CSS issues. This lofi music is really helping me focus',
      timestamp: new Date(Date.now() - 240000), // 4 minutes ago
    },
    {
      id: '3',
      userId: 'demo3',
      username: 'Mike',
      message: 'Anyone know good resources for learning TypeScript?',
      timestamp: new Date(Date.now() - 180000), // 3 minutes ago
    },
    {
      id: '4',
      userId: 'demo4',
      username: 'Emma',
      message: 'Check out the TypeScript handbook - it\'s really comprehensive!',
      timestamp: new Date(Date.now() - 120000), // 2 minutes ago
    }
  ];

  // Initialize chat
  useEffect(() => {
    if (isWalkthroughMode) {
      // Show demo messages in walkthrough mode
      setMessages(demoMessages);
      setIsConnected(true);
      setOnlineUsers(12);
    } else if (user && hasPaidSubscription) {
      // Initialize real chat connection
      initializeChat();
    }

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [user, hasPaidSubscription, isWalkthroughMode]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeChat = async () => {
    setIsLoading(true);
    try {
      // Load recent messages first
      const recentMessages = await loadRecentMessages(50);
      setMessages(recentMessages);
      
      // Get online user count
      const userCount = await getOnlineUserCount();
      setOnlineUsers(userCount);
      
      // Set up real-time subscription
      const unsubscribe = subscribeToChatMessages((newMessages) => {
        setMessages(newMessages);
      }, 50);
      
      unsubscribeRef.current = unsubscribe;
      setIsConnected(true);
      
      // Update online user count periodically
      const userCountInterval = setInterval(async () => {
        try {
          const count = await getOnlineUserCount();
          setOnlineUsers(count);
        } catch (error) {
          console.error('Error updating user count:', error);
        }
      }, 30000); // Update every 30 seconds
      
      // Store interval for cleanup
      return () => {
        clearInterval(userCountInterval);
      };
      
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      // Show a welcome message if loading fails
      setMessages([
        {
          id: 'welcome',
          userId: 'system',
          username: 'System',
          message: 'Welcome to the community chat! Share what you\'re working on today.',
          timestamp: new Date(),
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !user || isSending) return;

    const messageToSend = {
      userId: user.uid,
      username: user.displayName || 'Anonymous',
      message: newMessage.trim(),
      avatar: user.photoURL || undefined
    };

    // Clear input immediately for better UX
    const originalMessage = newMessage;
    setNewMessage('');
    setIsSending(true);

    try {
      await sendChatMessage(messageToSend);
      console.log('Message sent successfully');
    } catch (error) {
      console.error('Failed to send message:', error);
      // Restore the message if sending failed
      setNewMessage(originalMessage);
      // You could show an error toast here
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString();
  };

  const getUserInitials = (username: string) => {
    return username.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Helper function to determine if we should show timestamp for own messages
  const shouldShowOwnMessageTime = (currentIndex: number) => {
    const currentMessage = messages[currentIndex];
    const nextMessage = messages[currentIndex + 1];
    
    // Always show timestamp if it's not our message
    if (currentMessage.userId !== user?.uid) {
      return false; // We don't show timestamps for other users' messages anyway
    }
    
    // Show timestamp if it's the last message in the array
    if (currentIndex === messages.length - 1) {
      return true;
    }
    
    // Show timestamp if the next message is from a different user
    if (nextMessage && nextMessage.userId !== user?.uid) {
      return true;
    }
    
    // Don't show timestamp if the next message is also from us
    return false;
  };

  // Check if user has access to this tool
  const hasAccess = isWalkthroughMode || (user && hasPaidSubscription);

  if (!hasAccess) {
    return (
      <div className="chat-tab">
        <div className="chat-locked">
          <BsChatDots className="chat-lock-icon" />
          <h3>Community Chat</h3>
          <p>Connect with other focused creators and share what you're working on</p>
          <button onClick={onShowPaymentPopup} className="unlock-button">
            Join the Community
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-tab">
      {/* Tool Header Buttons */}
      <ToolHeader
        isPinned={isPinned}
        onTogglePin={onTogglePin}
        isFullscreen={isFullscreen}
        onToggleFullscreen={onToggleFullscreen}
        toolType="chat"
      />

      {/* Messages Area */}
      <div className="chat-messages">
        {isLoading ? (
          <div className="chat-loading">
            <div className="loading-spinner"></div>
            <span>Loading messages...</span>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div 
                key={message.id} 
                className={`message ${message.userId === user?.uid ? 'own-message' : ''} ${message.userId === 'system' ? 'system-message' : ''}`}
              >
                <div className="message-content">
                  {message.userId !== 'system' && message.userId !== user?.uid && (
                    <div className="message-header">
                      <span className="message-username">{message.username}</span>
                      <span className="message-time">{formatRelativeTime(message.timestamp)}</span>
                    </div>
                  )}
                  
                  <div className="message-text">{message.message}</div>
                  
                  {message.userId === user?.uid && shouldShowOwnMessageTime(index) && (
                    <div className="message-time own-time">{formatTime(message.timestamp)}</div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message Input */}
      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <input
            ref={messageInputRef}
            type="text"
            className="chat-input"
            placeholder={isWalkthroughMode ? "Try typing a message..." : "Share what you're working on..."}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={!isConnected || isWalkthroughMode || isSending}
          />
          <button 
            className="send-button"
            onClick={sendMessage}
            disabled={!newMessage.trim() || !isConnected || isWalkthroughMode || isSending}
          >
            <BsSend />
          </button>
        </div>
        
        {isWalkthroughMode && (
          <div className="walkthrough-notice">
            <span>💡 This is a preview - join to start chatting with the community!</span>
          </div>
        )}
        
        {isSending && (
          <div className="sending-notice">
            <span>Sending message...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatTab; 